import type { CorsOptions, HandlerContext, HandlerUtility } from "../types.js";

const DEFAULT_METHODS = ["GET", "HEAD", "PUT", "PATCH", "POST", "DELETE"];
const DEFAULT_HEADERS = ["Content-Type", "Authorization"];

/**
 * CORS utility for handling Cross-Origin Resource Sharing.
 *
 * @example
 * ```ts
 * import { compose, withCors } from "next-route-compose";
 *
 * export const GET = compose(
 *   withCors({ origin: "https://example.com" })
 * )(handler);
 *
 * // Allow any origin
 * export const POST = compose(
 *   withCors({ origin: "*", credentials: true })
 * )(handler);
 * ```
 */
export function withCors(options: CorsOptions = {}): HandlerUtility {
    const {
        origin = "*",
        methods = DEFAULT_METHODS,
        allowedHeaders = DEFAULT_HEADERS,
        exposedHeaders = [],
        credentials = false,
        maxAge = 86400, // 24 hours
    } = options;

    return (handler) => {
        return async (context: HandlerContext) => {
            const requestOrigin = context.request.headers.get("Origin") ?? "";

            // Calculate allowed origin
            let allowedOrigin: string;
            if (origin === "*") {
                allowedOrigin = "*";
            } else if (typeof origin === "string") {
                allowedOrigin = origin;
            } else if (Array.isArray(origin)) {
                allowedOrigin = origin.includes(requestOrigin) ? requestOrigin : "";
            } else if (typeof origin === "function") {
                allowedOrigin = origin(requestOrigin) ? requestOrigin : "";
            } else {
                allowedOrigin = "";
            }

            // Create CORS headers
            const corsHeaders: HeadersInit = {
                "Access-Control-Allow-Origin": allowedOrigin,
                "Access-Control-Allow-Methods": methods.join(", "),
                "Access-Control-Allow-Headers": allowedHeaders.join(", "),
            };

            if (exposedHeaders.length > 0) {
                corsHeaders["Access-Control-Expose-Headers"] = exposedHeaders.join(", ");
            }

            if (credentials) {
                corsHeaders["Access-Control-Allow-Credentials"] = "true";
            }

            if (maxAge > 0) {
                corsHeaders["Access-Control-Max-Age"] = String(maxAge);
            }

            // Handle preflight requests
            if (context.request.method === "OPTIONS") {
                return new Response(null, {
                    status: 204,
                    headers: corsHeaders,
                });
            }

            // Call the next handler and add CORS headers to response
            const response = await handler(context);

            // Clone response with CORS headers
            const newHeaders = new Headers(response.headers);
            for (const [key, value] of Object.entries(corsHeaders)) {
                newHeaders.set(key, value);
            }

            return new Response(response.body, {
                status: response.status,
                statusText: response.statusText,
                headers: newHeaders,
            });
        };
    };
}
